import { Container } from 'pixi.js';
import { Physics } from '@esotericsoftware/spine-pixi-v8';
import { createSpineInstance } from './spineLoader.js';
import { gameConfig } from '../config/gameConfig.js';
import { getSymbolDef } from '../config/infoPaytable.js';

function restoreIdleAnimation(spine) {
  if (!spine?.state) return;
  try {
    const idleName =
      gameConfig.animations?.idle && spine.skeleton.data.findAnimation(gameConfig.animations.idle)
        ? gameConfig.animations.idle
        : spine.skeleton.data.findAnimation('idle')
          ? 'idle'
          : null;
    if (idleName) {
      spine.state.setAnimation(0, idleName, true);
    }
  } catch {
    /* noop */
  }
}

/**
 * Fit a reel Spine symbol into a fixed info-tab box using skeleton data bounds so every
 * symbol shares the same centering rules (tight attachment AABBs vary per symbol).
 * @param {import('@esotericsoftware/spine-pixi-v8').Spine} spine
 * @param {number} boxW
 * @param {number} boxH
 */
function fitSpineInInfoBox(spine, boxW, boxH) {
  if (!spine?.skeleton) return;

  if (spine.state) {
    spine.state.clearTracks();
  }
  spine.x = 0;
  spine.y = 0;
  spine.skeleton.setToSetupPose();
  spine.skeleton.updateWorldTransform(Physics.update);

  const data = spine.skeleton.data;
  const dataX = typeof data.x === 'number' ? data.x : 0;
  const dataY = typeof data.y === 'number' ? data.y : 0;
  const dataW = typeof data.width === 'number' && data.width > 0 ? data.width : 0;
  const dataH = typeof data.height === 'number' && data.height > 0 ? data.height : 0;

  const baseScale = spine.scale.x || 1;

  let bw = dataW;
  let bh = dataH;
  if (bw <= 0 || bh <= 0) {
    const b = spine.getBounds();
    bw = Math.max(1, b.width / baseScale);
    bh = Math.max(1, b.height / baseScale);
  }

  const fitMul = Math.min(boxW / (bw * baseScale), boxH / (bh * baseScale));
  const s = baseScale * fitMul;
  spine.scale.set(s);

  if (dataW > 0 && dataH > 0) {
    const cx = dataX + dataW * 0.5;
    const cy = dataY + dataH * 0.5;
    spine.x = boxW * 0.5 - cx * s;
    spine.y = boxH * 0.5 - cy * s;
  } else {
    spine.skeleton.updateWorldTransform(Physics.update);
    const b = spine.getBounds();
    spine.x = (boxW - b.width) * 0.5 - b.x;
    spine.y = (boxH - b.height) * 0.5 - b.y;
  }

  restoreIdleAnimation(spine);
}

/**
 * Static idle symbol for the settings Information tab (uses live reel Spine art).
 * @param {number} symbolId
 * @param {Record<string | number, unknown>} spineAssets
 * @param {number} boxW
 * @param {number} boxH
 * @returns {Container}
 */
export function createInfoSymbolSprite(symbolId, spineAssets, boxW, boxH) {
  const wrap = new Container();
  wrap.eventMode = 'none';
  const spine = createSpineInstance(symbolId, spineAssets);
  if (!spine) return wrap;

  spine.eventMode = 'none';
  wrap.addChild(spine);
  fitSpineInInfoBox(spine, boxW, boxH);

  wrap.__width = boxW;
  wrap.__height = boxH;
  return wrap;
}

/**
 * @param {number} symbolId
 * @param {Record<string | number, unknown>} spineAssets
 * @param {number} boxW
 * @param {number} boxH
 * @param {{ fallbackLabel?: string, fallbackAccent?: number }} [opts]
 * @param {(label: string, w: number, h: number, accent?: number) => Container} makeIconBadge
 */
export function createInfoSymbolOrBadge(symbolId, spineAssets, boxW, boxH, opts, makeIconBadge) {
  const sprite = createInfoSymbolSprite(symbolId, spineAssets, boxW, boxH);
  if (sprite.children.length > 0) return sprite;
  const def = getSymbolDef(symbolId);
  const label = opts?.fallbackLabel ?? def?.spine?.toUpperCase() ?? String(symbolId);
  return makeIconBadge(label, boxW, boxH, opts?.fallbackAccent);
}
